import { useState, useEffect } from 'react';
import axios from 'axios';
import { ProductCard } from '@/components/ProductCard';
import { useAuth } from '@/context/AuthContext';
import { Zap, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FrequentlyOrdered = () => {
  const [frequentItems, setFrequentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();

  useEffect(() => {
    if (user && token) {
      fetchFrequentItems();
    }
  }, [user, token]);

  const fetchFrequentItems = async () => {
    try {
      const response = await axios.get(`${API}/analytics/frequently-ordered`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFrequentItems(response.data);
    } catch (error) {
      console.error('Failed to fetch frequent items:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || loading || frequentItems.length === 0) {
    return null;
  }

  return (
    <section className="py-12 bg-gradient-to-b from-white to-amber-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-primary/10 p-3 rounded-full">
            <Zap className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-accent" data-testid="frequently-ordered-title">
              Your Favorites - Quick Add!
            </h2>
            <p className="text-muted-foreground">Items you order most frequently</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {frequentItems.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const ProductRecommendations = ({ productId }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (productId) {
      fetchRecommendations();
    }
  }, [productId]);

  const fetchRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/analytics/recommendations/${productId}`);
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || recommendations.length === 0) {
    return null;
  }

  return (
    <section className="mt-12 pt-12 border-t border-amber-100">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-success/10 p-3 rounded-full">
          <TrendingUp className="h-6 w-6 text-success" />
        </div>
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-accent" data-testid="recommendations-title">
            Customers Also Bought
          </h2>
          <p className="text-muted-foreground">Frequently purchased together</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
          />
        ))}
      </div>
    </section>
  );
};
